import { useEffect, useRef } from 'react'
import * as THREE from 'three'
import { useFrame } from '@react-three/fiber'
import topographicVertexShader from './shaders/topographic/vertex.glsl'
import topographicFragmentShader from './shaders/topographic/fragment.glsl'
import { button, folder, useControls } from 'leva'

export default function TopographicMap()
{
    const meshRef = useRef(null)
    const segments = 100
    const uFading = 0.45
    const minPositionStrength = 0
    const maxPositionStrength = 0.5
    const minElevationStrength = 0
    const maxElevationStrength = 5
    const minWarpStrength = 0
    const maxWarpStrength = 5

    // Leva controls
    const {u2D, size, uSpeed} = useControls('Miscellaneous', {
        u2D:
        {
            value: false,
            label: 'Transform to 2D plane',
        },
        size:
        {
            value: 10,
            step: 1,
            min: 1,
            max: 25,
            label: 'Size of the map',
        },
        uSpeed:
        {
            value: 0.5,
            step: 0.01,
            min: 0,
            max: 5,
            label: 'Speed',
        },
    })

    const [{uPositionStrength, uElevationStrength, uWarpStrength}, set] = useControls('Topography', () => ({
        Randomize: button(() => {
            // Generate random values for the controls based on their min/max
            const randomPositionStrength = Math.random() * (maxPositionStrength - minPositionStrength) + minPositionStrength;
            const randomElevationStrength = Math.random() * (maxElevationStrength - minElevationStrength) + minElevationStrength;
            const randomWarpStrength = Math.random() * (maxWarpStrength - minWarpStrength) + minWarpStrength;

            // Set the controls to the new random values
            set({
              uPositionStrength: randomPositionStrength,
              uElevationStrength: randomElevationStrength,
              uWarpStrength: randomWarpStrength,
            })
        }),
        uPositionStrength:
        {
            value: 0.2,
            step: 0.01,
            min: minPositionStrength,
            max: maxPositionStrength,
            label: 'General topography',
        },
        uElevationStrength:
        {
            value: 3.0,
            step: 0.01,
            min: minElevationStrength,
            max: maxElevationStrength,
            label: 'Max. elevation',
        },
        uWarpStrength:
        {
            value: 0.5,
            step: 0.01,
            min: minWarpStrength,
            max: maxWarpStrength,
            label: 'Warping',
        },
    }))

    const [{uLineColorMode, uFancyLineColorMode, uLineColor, uBackgroundColor, uLineThickness, uLineHeight, uElevationColorMode, uHighColor, uLowColor, uElevationColorStrength, uELevationColorDiffuse}, setColor] = useControls('Color', () => ({
        Main: folder({
            uLineColorMode:
            {
                value: true,
                label: 'Activate line color mode?',
            },
            uFancyLineColorMode:
            {
                value: false,
                label: 'Activate FANCY line color mode?',
                render: (get) => get('Color.Main.uLineColorMode') === true, // Only show when line color mode is active
            },
            uLineColor:
            {
                value: '#edf2f4',
                label: "Line color",
                render: (get) => get('Color.Main.uLineColorMode') === true && (get('Color.Main.uFancyLineColorMode') === false), // Only show when line color mode is active AND fancy color mode is non-active
            },
            uLineThickness:
            {
                value: 0.10,
                step: 0.001,
                min: 0,
                max: 1,
                label: 'Line thickness',
                render: (get) => get('Color.Main.uLineColorMode') === true, // Only show when line color mode is active
            },
            uLineHeight:
            {
                value: 0.15,
                step: 0.01,
                min: 0,
                max: 1,
                label: 'Line height',
                render: (get) => get('Color.Main.uLineColorMode') === true, // Only show when line color mode is active
            },
            uBackgroundColor:
            {
                value: '#2b2d42',
                label: "Background color",
            },
        }),
        Elevation: folder({
            uElevationColorMode:
            {
                value: true,
                label: 'Activate elevation color mode?',
            },
            uHighColor:
            {
                value: '#ff0000',
                label: "High elevation color",
                render: (get) => get('Color.Elevation.uElevationColorMode') === true, // Only show when elevation color mode is active
            },
            uLowColor:
            {
                value: '#0000ff',
                label: "Low elevation color",
                render: (get) => get('Color.Elevation.uElevationColorMode') === true, // Only show when elevation color mode is active
            },
            uElevationColorStrength:
            {
                value: 1.5,
                step: 0.001,
                min: 0.5,
                max: 2,
                label: 'Elevation color strength',
                render: (get) => get('Color.Elevation.uElevationColorMode') === true, // Only show when elevation color mode is active
            },
            uELevationColorDiffuse:
            {
                value: 2,
                step: 0.01,
                min: 0,
                max: 5,
                label: 'Elevation color diffusion',
                render: (get) => get('Color.Elevation.uElevationColorMode') === true, // Only show when elevation color mode is active
            },
        }),
    }))

    const uniforms = useRef({
        u2D: new THREE.Uniform(+!u2D),
        uTime: new THREE.Uniform(0.0),
        uSpeed: new THREE.Uniform(uSpeed),
        uPositionStrength: new THREE.Uniform(uPositionStrength),
        uElevationStrength: new THREE.Uniform(uElevationStrength),
        uWarpStrength: new THREE.Uniform(uWarpStrength),
        uLineColorMode: new THREE.Uniform(+uLineColorMode),
        uFancyLineColorMode: new THREE.Uniform(+uFancyLineColorMode),
        uLineColor: new THREE.Uniform(new THREE.Color(uLineColor)),
        uBackgroundColor: new THREE.Uniform(new THREE.Color(uBackgroundColor)),
        uFading: new THREE.Uniform(uFading),
        uLineThickness: new THREE.Uniform(uLineThickness),
        uLineHeight: new THREE.Uniform(uLineHeight),
        uMaxHeight: new THREE.Uniform(((1.0 / 2.0) ** 1.1) * uElevationStrength),
        uElevationColorMode: new THREE.Uniform(+uElevationColorMode),
        uHighColor: new THREE.Uniform(new THREE.Color(uHighColor)),
        uLowColor: new THREE.Uniform(new THREE.Color(uLowColor)),
        uElevationColorStrength: new THREE.Uniform(uElevationColorStrength),
        uELevationColorDiffuse: new THREE.Uniform(uELevationColorDiffuse),
    })

    useFrame(({clock}) => {
        if (meshRef.current) {
            meshRef.current.material.uniforms.uTime.value = clock.getElapsedTime()
        }
    })

    useEffect(() => {
        if (meshRef.current) {
            meshRef.current.geometry.dispose(); // Dispose of the old geometry to prevent memory leaks
            meshRef.current.geometry = new THREE.PlaneGeometry(size, size, size * segments, size * segments);
        }
    }, [size])

    useEffect(() => {
        if (meshRef.current) {
            meshRef.current.material.uniforms.u2D.value = +!u2D
            meshRef.current.material.uniforms.uSpeed.value = uSpeed
            meshRef.current.material.uniforms.uPositionStrength.value = uPositionStrength
            meshRef.current.material.uniforms.uElevationStrength.value = uElevationStrength
            meshRef.current.material.uniforms.uWarpStrength.value = uWarpStrength
            meshRef.current.material.uniforms.uLineColorMode.value = +uLineColorMode
            meshRef.current.material.uniforms.uFancyLineColorMode.value = +uFancyLineColorMode
            meshRef.current.material.uniforms.uLineColor.value = new THREE.Color(uLineColor)
            meshRef.current.material.uniforms.uBackgroundColor.value = new THREE.Color(uBackgroundColor)
            meshRef.current.material.uniforms.uFading.value = uFading
            meshRef.current.material.uniforms.uLineThickness.value = uLineThickness
            meshRef.current.material.uniforms.uLineHeight.value = uLineHeight
            meshRef.current.material.uniforms.uMaxHeight.value = ((1.0 / 2.0) ** 1.1) * uElevationStrength
            meshRef.current.material.uniforms.uElevationColorMode.value = +uElevationColorMode
            meshRef.current.material.uniforms.uHighColor.value = new THREE.Color(uHighColor)
            meshRef.current.material.uniforms.uLowColor.value = new THREE.Color(uLowColor)
            meshRef.current.material.uniforms.uElevationColorStrength = new THREE.Uniform(uElevationColorStrength)
            meshRef.current.material.uniforms.uELevationColorDiffuse = new THREE.Uniform(uELevationColorDiffuse)
            
            // // Set the background color of the #root element
            // const rootElement = document.getElementById('root')
            // if (rootElement) {
            //   rootElement.style.backgroundColor = uBackgroundColor
            // }
            // console.log(uBackgroundColor)
            // console.log(new THREE.Color(uBackgroundColor))
        }
    }, [u2D, uSpeed, uPositionStrength, uElevationStrength, uWarpStrength, uLineColorMode, uFancyLineColorMode, uLineColor, uBackgroundColor, uFading, uLineThickness, uLineHeight, uElevationColorMode, uHighColor, uLowColor, uElevationColorStrength, uELevationColorDiffuse])
    

    return (
        <mesh
            ref={meshRef}
            rotation-x={- Math.PI / 2}
        >
            <planeGeometry args={[size, size, size * segments, size * segments]} />
            <shaderMaterial
                vertexShader={topographicVertexShader}
                fragmentShader={topographicFragmentShader}
                uniforms={uniforms.current}
                transparent={true}
            />
        </mesh>
    )
}